import React, { useCallback, useMemo } from "react";
import {
  countRowPermissions,
  PERM_ACTIONS,
} from "./permissionMatrixConfig";

function IconEye() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function IconPlus() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M12 5v14M5 12h14" strokeLinecap="round" />
    </svg>
  );
}

function IconEdit() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconTrash() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const PERM_ICONS = {
  can_view: IconEye,
  can_create: IconPlus,
  can_edit: IconEdit,
  can_delete: IconTrash,
};

function AccessPill({ action, checked, disabled, label, onToggle }) {
  const Icon = PERM_ICONS[action.key] || IconEye;
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-label={`${action.label} — ${label}`}
      title={action.label}
      disabled={disabled}
      className={`ub-access-pill ub-access-pill--${action.tone}${checked ? " ub-access-pill--on" : ""}`}
      onClick={() => onToggle(!checked)}
    >
      <span className="ub-access-pill__icon">
        <Icon />
      </span>
      <span className="ub-access-pill__text">{action.label}</span>
    </button>
  );
}

function AccessCard({
  row,
  disabled,
  rowLocked,
  onToggle,
  onSetRow,
}) {
  const granted = countRowPermissions(row);
  const total = PERM_ACTIONS.length;
  const pct = Math.round((granted / total) * 100);

  return (
    <article
      className={`ub-access-card${rowLocked ? " ub-access-card--locked" : ""}${granted ? " ub-access-card--active" : ""}`}
    >
      <div className="ub-access-card__main">
        <div className="ub-access-card__identity">
          <h4 className="ub-access-card__title">{row._label}</h4>
          {row._sublabel ? (
            <p className="ub-access-card__slug">{row._sublabel}</p>
          ) : null}
          {rowLocked ? (
            <span className="ub-access-card__lock">Fixed for admin</span>
          ) : null}
        </div>
        <div className="ub-access-card__pills" role="group" aria-label={`Permissions for ${row._label}`}>
          {PERM_ACTIONS.map((action) => (
            <AccessPill
              key={action.key}
              action={action}
              checked={Number(row[action.key]) === 1}
              disabled={disabled || rowLocked}
              label={row._label}
              onToggle={(next) => onToggle(row._rowKey, action.key, next)}
            />
          ))}
        </div>
      </div>

      <footer className="ub-access-card__foot">
        <div className="ub-access-card__meter" aria-hidden>
          <div className="ub-access-card__meter-fill" style={{ width: `${pct}%` }} />
        </div>
        <span className="ub-access-card__count">
          {granted}/{total}
        </span>
        {!rowLocked && !disabled ? (
          <div className="ub-access-card__quick">
            <button
              type="button"
              className="ub-access-quick"
              onClick={() => onSetRow(row._rowKey, "full")}
            >
              All
            </button>
            <button
              type="button"
              className="ub-access-quick"
              onClick={() => onSetRow(row._rowKey, "view")}
            >
              View only
            </button>
            <button
              type="button"
              className="ub-access-quick ub-access-quick--muted"
              onClick={() => onSetRow(row._rowKey, "none")}
            >
              Clear
            </button>
          </div>
        ) : null}
      </footer>
    </article>
  );
}

export function AccessMatrixLegend({
  rows,
  nameColumn = "Item",
  disabled,
  isRowDisabled,
  onPatchColumn,
  onToggle,
}) {
  const columnState = useMemo(() => {
    const eligible = (rows || []).filter((r) => !isRowDisabled?.(r));
    return PERM_ACTIONS.map((action) => {
      const onCount = eligible.filter((r) => Number(r[action.key]) === 1).length;
      return {
        action,
        allOn: eligible.length > 0 && onCount === eligible.length,
        someOn: onCount > 0 && onCount < eligible.length,
        onCount,
        total: eligible.length,
      };
    });
  }, [rows, isRowDisabled]);

  const toggleColumn = useCallback(
    (permKey, turnOn) => {
      if (onPatchColumn) {
        onPatchColumn(permKey, turnOn, isRowDisabled);
        return;
      }
      for (const row of rows || []) {
        if (isRowDisabled?.(row)) continue;
        onToggle(row._rowKey, permKey, turnOn);
      }
    },
    [onPatchColumn, rows, isRowDisabled, onToggle],
  );

  return (
    <header className="ub-access-matrix__legend">
      <span className="ub-access-matrix__legend-label">{nameColumn}</span>
      <div className="ub-access-matrix__legend-cols">
        {columnState.map(({ action, allOn, someOn, onCount, total }) => (
          <button
            key={action.key}
            type="button"
            className={`ub-access-col${allOn ? " ub-access-col--all" : ""}${someOn ? " ub-access-col--some" : ""}`}
            disabled={disabled || total === 0}
            title={`Toggle ${action.label} for all ${total} items`}
            aria-pressed={allOn}
            onClick={() => toggleColumn(action.key, !allOn)}
          >
            <span className={`ub-access-col__icon ub-access-col__icon--${action.tone}`}>
              {React.createElement(PERM_ICONS[action.key])}
            </span>
            <span className="ub-access-col__label">{action.label}</span>
            <span className="ub-access-col__meta">
              {onCount}/{total}
            </span>
          </button>
        ))}
      </div>
    </header>
  );
}

export default function PermissionMatrixTable({
  rows,
  nameColumn = "Item",
  onToggle,
  onPatchRow,
  onPatchColumn,
  disabled,
  isRowDisabled,
  compact = false,
  showLegend = true,
}) {
  const setRowPreset = useCallback(
    (rowKey, preset) => {
      const patch =
        preset === "full"
          ? { can_view: 1, can_create: 1, can_edit: 1, can_delete: 1 }
          : preset === "view"
            ? { can_view: 1, can_create: 0, can_edit: 0, can_delete: 0 }
            : { can_view: 0, can_create: 0, can_edit: 0, can_delete: 0 };

      if (onPatchRow) {
        onPatchRow(rowKey, patch);
        return;
      }
      for (const action of PERM_ACTIONS) {
        onToggle(rowKey, action.key, Boolean(patch[action.key]));
      }
    },
    [onPatchRow, onToggle],
  );

  if (!rows?.length) {
    return <p className="ub-muted">No items in catalog.</p>;
  }

  return (
    <div
      className={`ub-access-matrix${compact ? " ub-access-matrix--compact" : ""}`}
    >
      {showLegend ? (
        <AccessMatrixLegend
          rows={rows}
          nameColumn={nameColumn}
          disabled={disabled}
          isRowDisabled={isRowDisabled}
          onPatchColumn={onPatchColumn}
          onToggle={onToggle}
        />
      ) : null}

      <div className="ub-access-matrix__list">
        {rows.map((row) => (
          <AccessCard
            key={row._rowKey}
            row={row}
            disabled={disabled}
            rowLocked={Boolean(isRowDisabled?.(row))}
            onToggle={onToggle}
            onSetRow={setRowPreset}
          />
        ))}
      </div>
    </div>
  );
}
